import React, { useMemo, useState, useEffect } from "react";
import type { BridgeMatrix } from "../types";
import {
  shortestAllowedPath,
  type BridgeMatrixEdge,
} from "../bridgeMatrixIndex";
import { DemoWorkbenchCard, DemoWorkbenchPill } from "@csk/demo-ui";

const EMPTY_EDGES: readonly BridgeMatrixEdge[] = [];

export function PathExplorer(props: {
  matrix: BridgeMatrix | null;
  selection?: { start: string; goal: string } | null;
}): React.JSX.Element {
  if (!props.matrix)
    return (
      <DemoWorkbenchCard title="Loading">Matrix required</DemoWorkbenchCard>
    );
  const matrix = props.matrix;
  const { adjacency, nodes } = matrix.index;
  const [start, setStart] = useState(props.selection?.start ?? nodes[0]);
  const [goal, setGoal] = useState(props.selection?.goal ?? nodes[1]);

  useEffect(() => {
    if (props.selection?.start !== undefined) setStart(props.selection.start);
    if (props.selection?.goal !== undefined) setGoal(props.selection.goal);
  }, [props.selection?.start, props.selection?.goal]);

  const path = useMemo(
    () =>
      start && goal ? shortestAllowedPath(matrix.index, start, goal) : null,
    [matrix.index, start, goal],
  );

  const outEdges = adjacency.get(start) ?? EMPTY_EDGES;
  const { allowedOut, forbiddenOut } = useMemo(() => {
    const allowed: string[] = [];
    const forbidden: string[] = [];
    for (const edge of outEdges) {
      if (edge.status === "allowed") allowed.push(edge.to);
      else forbidden.push(edge.to);
    }
    return {
      allowedOut: allowed.sort(),
      forbiddenOut: forbidden.sort(),
    };
  }, [outEdges]);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <DemoWorkbenchCard title="Select bridges">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-slate-300 mb-1">Start</div>
            <select
              className="w-full rounded-lg bg-slate-950/40 border border-slate-800 px-2 py-1"
              value={start}
              onChange={(e) => {
                setStart(e.target.value);
              }}
            >
              {nodes.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="text-slate-300 mb-1">Goal</div>
            <select
              className="w-full rounded-lg bg-slate-950/40 border border-slate-800 px-2 py-1"
              value={goal}
              onChange={(e) => {
                setGoal(e.target.value);
              }}
            >
              {nodes.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 text-sm text-slate-300">
          <div className="font-semibold text-slate-100">Outgoing (start)</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {allowedOut.map((t) => (
              <DemoWorkbenchPill key={"a" + t}>allow → {t}</DemoWorkbenchPill>
            ))}
            {forbiddenOut.map((t) => (
              <DemoWorkbenchPill key={"f" + t}>forbid → {t}</DemoWorkbenchPill>
            ))}
          </div>
        </div>
      </DemoWorkbenchCard>

      <DemoWorkbenchCard
        title="Shortest allowed path"
        right={<DemoWorkbenchPill>allowed edges</DemoWorkbenchPill>}
      >
        {!path ? (
          <div className="text-sm text-slate-300">
            No allowed path exists. This is a structural statement: the exploit
            geometry is unrepresentable unless a new bridge/rule is admitted.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {path.map((p, i) => (
              <React.Fragment key={p}>
                <DemoWorkbenchPill>{p}</DemoWorkbenchPill>
                {i < path.length - 1 ? (
                  <span className="text-slate-500">→</span>
                ) : null}
              </React.Fragment>
            ))}
          </div>
        )}
        <div className="text-xs text-slate-400 mt-3">
          This explorer visualizes composition governance: admissible execution
          paths are exactly those admitted by the matrix.
        </div>
      </DemoWorkbenchCard>
    </div>
  );
}
